import { useEffect, useMemo, useState } from 'react'
import { AnalysisClientModal, ModalClient } from '../viewThruBatchView/AnalysisClientModal'
import { money } from '../viewThruBatchView/BatchAnalysis'
import { BatchRun, count, downloadCsv, formatDateTime } from '../viewThruBatchView/shared'
import tableStyles from '../viewThruBatchView/runsTable.module.scss'
import { CcjRule, ccjRuleLabel } from './ccjRules'
import {
    IMPORTED_SCOPES,
    ImportedReportRow,
    ImportedScope,
    buildImportedReport,
    buildImportedReportCsv,
    filterImportedRows,
    importedReportFilename,
} from './importedReport'
import { batchLabel } from './useAllBatchesAnalysis'
import { useImportedReport } from './useImportedReport'
import styles from './styles.module.scss'

interface Props {
    batches: BatchRun[]
    ccjRule: CcjRule
}

const REPORT_COLUMNS: { key: string; width?: number }[] = [
    { key: 'client' },
    { key: 'email', width: 210 },
    { key: 'status', width: 150 },
    { key: 'imported', width: 130 },
    { key: 'agreements', width: 96 },
    { key: 'value', width: 100 },
    { key: 'srds', width: 170 },
    { key: 'eligibility' },
]

const OUTCOME_TEXT = { eligible: 'Eligible', ineligible: 'Not eligible', duplicate: 'Duplicate' } as const

const statusBadgeClass = (status: ImportedReportRow['status']) =>
    status === 'imported'
        ? tableStyles.statusComplete
        : status === 'elsewhere'
        ? tableStyles.statusDuplicate
        : tableStyles.statusFailed

/**
 * Bookkeeping report for one batch: which of its clients are in Sentinel
 * Reporting, matched on the reference the import gives them. Imported clients
 * are the report; people in SRDS from elsewhere and people not imported are
 * listed alongside, with the analysis verdict so a missing eligible client
 * stands out.
 */
export const ImportedReportPanel = ({ batches, ccjRule }: Props) => {
    const report = useImportedReport()
    const [batchId, setBatchId] = useState('')
    const [scope, setScope] = useState<ImportedScope>('imported')
    const [openClient, setOpenClient] = useState<ModalClient | null>(null)

    // Default to the newest batch once the list arrives; keep the choice if it is still listed.
    useEffect(() => {
        if (!batches.length) return
        setBatchId((current) => (current && batches.some((run) => run.id === current) ? current : batches[0].id))
    }, [batches])

    const { state } = report
    const reportBatch = state.status === 'idle' ? null : batches.find((run) => run.id === state.batchId) || null
    const built = useMemo(
        () => (state.status === 'complete' ? buildImportedReport({ matches: state.matches, analysis: state.analysis }) : null),
        [state]
    )
    const visibleRows = built ? filterImportedRows(built, scope) : []
    const stale = state.status === 'complete' && (state.batchId !== batchId || state.ccjRule !== ccjRule)

    const runReport = () => {
        if (!batchId) return
        setScope('imported')
        void report.run(batchId, ccjRule)
    }

    const exportScope = (exportFor: ImportedScope) => {
        if (!built || state.status !== 'complete' || !reportBatch) return
        const rows = filterImportedRows(built, exportFor)
        if (!rows.length) return
        downloadCsv(
            importedReportFilename(batchLabel(reportBatch), exportFor, state.ranAt),
            buildImportedReportCsv(rows, {
                batchId: reportBatch.id,
                batchLabel: batchLabel(reportBatch),
                batchOrigin: reportBatch.origin || state.matches.batch?.origin || '',
                ranAt: state.ranAt,
            })
        )
    }

    const openRow = (row: ImportedReportRow) => {
        if (!row.client) return
        setOpenClient({ ...row.client, outcome: row.analysisOutcome || undefined })
    }

    const reportLabel = reportBatch ? batchLabel(reportBatch) : 'the batch'

    return (
        <section
            className={`${tableStyles.importPanel} ${styles.reportPanel}`}
            aria-labelledby="viewthru-imported-title"
            aria-busy={report.running}
        >
            <div>
                <h4 id="viewthru-imported-title">Bookkeeping report — clients imported into Sentinel Reporting</h4>
                <p>
                    Pick one batch and run the report. It analyses the batch and, at the same time, looks every client
                    up in Sentinel Reporting (SRDS) by the reference the import gives them, so the list of who was
                    imported from this batch — with the agreements and value that went in — is exact. Clients whose
                    email is in SRDS under another record, and clients not imported at all, are shown alongside with
                    the analysis verdict, so anyone eligible who is missing stands out.
                </p>
            </div>

            <div className={`${tableStyles.importFields} ${styles.reportControls}`}>
                <label className={styles.reportBatchField}>
                    <span className={tableStyles.detailLabel}>Batch</span>
                    <select
                        className={styles.reportSelect}
                        value={batchId}
                        onChange={(event) => setBatchId(event.target.value)}
                        disabled={report.running || !batches.length}
                        aria-label="Batch to report on"
                    >
                        {!batches.length && <option value="">No completed batches</option>}
                        {batches.map((run) => (
                            <option key={run.id} value={run.id}>
                                {batchLabel(run)} — {run.origin || 'No origin'} · {formatDateTime(run.created_at)}
                            </option>
                        ))}
                    </select>
                </label>
                <button type="button" onClick={runReport} disabled={!batchId || report.running}>
                    {report.running
                        ? 'Running report…'
                        : state.status === 'complete' && state.batchId === batchId
                        ? 'Run report again'
                        : 'Run report'}
                </button>
            </div>

            {state.status === 'running' && (
                <div className={tableStyles.importProgress} role="status" aria-live="polite">
                    <div>
                        <strong>
                            <span className={tableStyles.statusSpinner} aria-hidden="true" /> Analysing {reportLabel} and
                            matching it against Sentinel Reporting
                        </strong>
                        <span>Both run together; the match is what the report is built on.</span>
                    </div>
                </div>
            )}

            {state.status === 'failed' && (
                <p className={tableStyles.detailError} role="alert">
                    Report for {reportLabel} failed: {state.error}
                </p>
            )}

            {state.status === 'complete' && built && reportBatch && (
                <div className={styles.reportResults}>
                    {stale && (
                        <p className={styles.reportStale} role="status">
                            {state.batchId !== batchId
                                ? `This report is for ${reportLabel}. Run the report to see the batch now selected.`
                                : 'The CCJ rule has changed since this report was run; the eligibility verdicts shown are under the old rule. Run it again to refresh them.'}
                        </p>
                    )}
                    {state.analysisError && (
                        <p className={styles.reportStale} role="status">
                            The SRDS match completed but the eligibility analysis failed ({state.analysisError}), so no
                            verdicts are shown next to the clients.
                        </p>
                    )}

                    <div className={styles.reportHeader}>
                        <div>
                            <strong className={styles.reportTitle}>{reportLabel}</strong>
                            <span className={styles.reportMeta}>
                                {reportBatch.origin || 'No origin'} · run {formatDateTime(state.ranAt.toISOString())} ·{' '}
                                {built.rows.length} {built.rows.length === 1 ? 'client' : 'clients'} in the batch
                                {state.analysis ? ` · analysed under ${ccjRuleLabel(state.ccjRule)}` : ''}
                            </span>
                        </div>
                        <div className={tableStyles.analysisExports}>
                            {IMPORTED_SCOPES.map((entry) => {
                                const n = filterImportedRows(built, entry.scope).length
                                const primary = entry.scope === 'imported'
                                return (
                                    <button
                                        key={entry.scope}
                                        type="button"
                                        className={primary ? undefined : styles.reportSecondaryExport}
                                        disabled={!n}
                                        onClick={() => exportScope(entry.scope)}
                                        title={
                                            primary
                                                ? 'The report: every client imported from this batch, with what went in'
                                                : `For reference: ${entry.label.toLowerCase()}`
                                        }
                                    >
                                        {primary ? `Export imported clients CSV (${n})` : `${entry.label} CSV (${n})`}
                                    </button>
                                )
                            })}
                        </div>
                    </div>

                    <div className={tableStyles.analysisSummary}>
                        {(
                            [
                                ['Imported from this batch', String(built.imported.length)],
                                ['Agreements imported', String(built.totals.importedLeads)],
                                ['Value imported', money(built.totals.importedValue)],
                                ['In SRDS from elsewhere', String(built.elsewhere.length)],
                                ['Not imported', String(built.notImported.length)],
                                ...(state.analysis
                                    ? ([['Eligible but not imported', String(built.totals.notImportedEligible)]] as [string, string][])
                                    : []),
                            ] as [string, string][]
                        ).map(([label, value]) => (
                            <div key={label} className={tableStyles.summaryCard} role="group" aria-label={label}>
                                <span className={tableStyles.detailLabel}>{label}</span>
                                <span className={tableStyles.detailValue}>{value}</span>
                            </div>
                        ))}
                    </div>

                    <div className={styles.reportFilters} role="group" aria-label="Show clients">
                        {IMPORTED_SCOPES.map((entry) => {
                            const n = filterImportedRows(built, entry.scope).length
                            return (
                                <button
                                    key={entry.scope}
                                    type="button"
                                    className={scope === entry.scope ? styles.reportFilterActive : undefined}
                                    aria-pressed={scope === entry.scope}
                                    onClick={() => setScope(entry.scope)}
                                >
                                    {entry.scope === 'all' ? 'All clients' : entry.label} <span>{count(n)}</span>
                                </button>
                            )
                        })}
                    </div>

                    <div className={tableStyles.tableWrapper}>
                        <table
                            className={`${tableStyles.runsTable} ${styles.batchTable} ${styles.clientTable} ${styles.reportTable}`}
                            aria-label="Imported clients report"
                        >
                            <colgroup>
                                {REPORT_COLUMNS.map((column) => (
                                    <col key={column.key} style={column.width ? { width: column.width } : undefined} />
                                ))}
                            </colgroup>
                            <thead>
                                <tr>
                                    <th>Client</th>
                                    <th>Email</th>
                                    <th>Sentinel Reporting</th>
                                    <th>Imported</th>
                                    <th className={styles.numCell} title="Agreements imported / agreements in the batch">
                                        Agreements
                                    </th>
                                    <th className={styles.numCell}>Value</th>
                                    <th>SRDS record</th>
                                    <th>Eligibility</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleRows.length === 0 ? (
                                    <tr>
                                        <td colSpan={REPORT_COLUMNS.length} className={styles.reportEmpty}>
                                            {scope === 'imported'
                                                ? 'No clients from this batch have been imported into Sentinel Reporting.'
                                                : scope === 'elsewhere'
                                                ? 'No clients in this batch are in Sentinel Reporting under another record.'
                                                : scope === 'not_imported'
                                                ? 'Every client in this batch is in Sentinel Reporting.'
                                                : 'No clients to report on in this batch.'}
                                        </td>
                                    </tr>
                                ) : (
                                    visibleRows.map((row) => {
                                        const clickable = Boolean(row.client)
                                        const open = () => openRow(row)
                                        return (
                                            <tr
                                                key={row.key}
                                                className={clickable ? styles.clientRow : undefined}
                                                onClick={clickable ? open : undefined}
                                                onKeyDown={
                                                    clickable
                                                        ? (event) => {
                                                              if (event.key === 'Enter' || event.key === ' ') {
                                                                  event.preventDefault()
                                                                  open()
                                                              }
                                                          }
                                                        : undefined
                                                }
                                                tabIndex={clickable ? 0 : undefined}
                                                role={clickable ? 'button' : undefined}
                                                aria-label={
                                                    clickable ? `Open ${row.name || 'client'} details and PDFs` : undefined
                                                }
                                                title={clickable ? 'Open client data and PDF reports' : undefined}
                                            >
                                                <td>
                                                    <span className={clickable ? styles.clientName : undefined}>
                                                        {row.name || 'Unnamed client'}
                                                    </span>
                                                    <span className={styles.cellMeta}>{row.clientRef}</span>
                                                </td>
                                                <td title={row.email || undefined}>{row.email || '—'}</td>
                                                <td>
                                                    <span className={`${tableStyles.badge} ${statusBadgeClass(row.status)}`}>
                                                        {row.status === 'imported'
                                                            ? 'Imported'
                                                            : row.status === 'elsewhere'
                                                            ? 'Elsewhere'
                                                            : 'Not imported'}
                                                    </span>
                                                    {row.status === 'elsewhere' && row.matchedOn === 'email' && (
                                                        <span className={styles.cellMeta}>matched on email</span>
                                                    )}
                                                </td>
                                                <td>
                                                    {row.srds?.importedAt ? (
                                                        formatDateTime(row.srds.importedAt)
                                                    ) : (
                                                        <span className={styles.numMuted}>—</span>
                                                    )}
                                                </td>
                                                <td className={styles.numCell}>
                                                    {row.srds ? (
                                                        <>
                                                            {row.srds.leads}
                                                            <span className={styles.cellMeta}>
                                                                of {row.agreementCount} in batch
                                                                {row.srds.leadsCancelled ? ` · ${row.srds.leadsCancelled} cancelled` : ''}
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span className={styles.numMuted}>—</span>
                                                            <span className={styles.cellMeta}>{row.agreementCount} in batch</span>
                                                        </>
                                                    )}
                                                </td>
                                                <td className={styles.numCell}>
                                                    {row.srds ? money(row.srds.value) : <span className={styles.numMuted}>—</span>}
                                                </td>
                                                <td title={row.srds?.origin || undefined}>
                                                    {row.srds ? (
                                                        <>
                                                            <span>{row.srds.clientRef || `client ${row.srds.clientId}`}</span>
                                                            <span className={styles.cellMeta}>
                                                                {row.srds.origin || 'Unknown origin'}
                                                                {row.otherRecords
                                                                    ? ` · +${row.otherRecords} more on this email`
                                                                    : ''}
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <span className={styles.numMuted}>—</span>
                                                    )}
                                                </td>
                                                <td className={styles.alsoFailedCell}>
                                                    {row.analysisOutcome ? (
                                                        <>
                                                            <span
                                                                className={`${styles.reasonChip} ${
                                                                    row.analysisOutcome === 'eligible'
                                                                        ? styles.reasonChipEligible
                                                                        : row.analysisOutcome === 'ineligible'
                                                                        ? styles.reasonChipBlocking
                                                                        : styles.reasonChipWaived
                                                                }`}
                                                            >
                                                                {OUTCOME_TEXT[row.analysisOutcome]}
                                                            </span>
                                                            {row.analysisReasons.map((reason) => (
                                                                <span key={reason} className={styles.reasonChip}>
                                                                    {reason}
                                                                </span>
                                                            ))}
                                                        </>
                                                    ) : (
                                                        <span className={styles.numMuted}>—</span>
                                                    )}
                                                </td>
                                            </tr>
                                        )
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                    <p className={tableStyles.tableFootnote}>
                        {scope === 'imported'
                            ? `${built.imported.length === 1 ? 'This client was' : `These ${built.imported.length} clients were`} imported from this batch — their Sentinel Reporting reference is the one this batch gives them.`
                            : scope === 'elsewhere'
                            ? 'These clients are in Sentinel Reporting under a different record, matched on email — an earlier batch or another route.'
                            : scope === 'not_imported'
                            ? 'Nothing in Sentinel Reporting for these clients, by reference or by email.'
                            : 'Every client the batch processed, whatever their standing in Sentinel Reporting.'}{' '}
                        Click a client to see their data and open the credit and AML PDFs.
                    </p>
                </div>
            )}

            {openClient && reportBatch && (
                <AnalysisClientModal
                    client={openClient}
                    batchId={reportBatch.id}
                    batchLabel={batchLabel(reportBatch)}
                    onClose={() => setOpenClient(null)}
                />
            )}
        </section>
    )
}
